import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Server } from 'lucide-react';
import { EditableCardActions } from './EditableCardActions';
import { useEditableCard } from '@/hooks/useEditableCard';
import { updateSchool } from '@/api/schools';
import { useAuth } from '@/contexts/AuthContext';
import type { SchoolEditData, UpdateSchoolData } from '@/types/school';

interface V1DatabaseCardProps {
  school: SchoolEditData;
  onSaved: () => void;
}

interface V1DatabaseFormData {
  [key: string]: unknown;
  dbHost: string | null;
  dbName: string | null;
  dbUser: string | null;
  dbPort: number | null;
  dbPassword: string | null;
}

export function V1DatabaseCard({ school, onSaved }: V1DatabaseCardProps) {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  const initialData: V1DatabaseFormData = {
    dbHost: school.dbHost ?? null,
    dbName: school.dbName ?? null,
    dbUser: school.dbUser ?? null,
    dbPort: school.dbPort ?? null,
    dbPassword: null,
  };

  const { isEditing, formData, isSaving, error, startEditing, cancelEditing, updateField, save } =
    useEditableCard<V1DatabaseFormData>({
      initialData,
      onSave: async (data) => {
        const payload: UpdateSchoolData = {
          dbHost: data.dbHost,
          dbName: data.dbName,
          dbUser: data.dbUser,
          dbPort: data.dbPort,
          dbPassword: data.dbPassword,
        };
        await updateSchool(school.code, payload);
      },
      onSaved,
    });

  return (
    <Card>
      <CardHeader
        icon={<Server />}
        iconColor="purple"
        actions={
          isAdmin && (
            <EditableCardActions
              isEditing={isEditing}
              isSaving={isSaving}
              onEdit={startEditing}
              onSave={save}
              onCancel={cancelEditing}
            />
          )
        }
      >
        <CardTitle>Base de données V1</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {isEditing ? (
          <>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
              <p className="text-sm text-amber-800">
                <strong>Attention :</strong> La modification de ces parametres peut entrainer des
                problemes de connexion a la base de donnees.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="db-host">Hote</Label>
                <Input
                  id="db-host"
                  value={formData.dbHost || ''}
                  onChange={(e) => updateField('dbHost', e.target.value || null)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="db-name">Nom de la base</Label>
                <Input
                  id="db-name"
                  value={formData.dbName || ''}
                  onChange={(e) => updateField('dbName', e.target.value || null)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="db-user">Utilisateur</Label>
                <Input
                  id="db-user"
                  value={formData.dbUser || ''}
                  onChange={(e) => updateField('dbUser', e.target.value || null)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="db-port">Port</Label>
                <Input
                  id="db-port"
                  type="number"
                  value={formData.dbPort || 3306}
                  onChange={(e) => updateField('dbPort', parseInt(e.target.value) || 3306)}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="db-password">
                  Nouveau mot de passe (laisser vide pour conserver)
                </Label>
                <Input
                  id="db-password"
                  type="password"
                  onChange={(e) => updateField('dbPassword', e.target.value || null)}
                  placeholder="••••••••"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Hôte</p>
                <p className="font-mono text-sm">{school.dbHost || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Port</p>
                <p className="font-mono text-sm">{school.dbPort || '3306'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Nom de la BDD</p>
                <p className="font-mono text-sm">{school.dbName || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Utilisateur</p>
                <p className="font-mono text-sm">{school.dbUser || '-'}</p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
